import {
  Button,
  Card,
  FormControl,
  FormHelperText,
  IconButton,
  SvgIcon,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { RiDeleteBinLine } from '@remixicon/react';
import { useMutation } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import CustomChip from '../../../../components/CustomChip';
import FileUploader from '../../../../components/FileUploader';
import InputHeader from '../../../../components/InputHeader';
import RejectionNotesTimeline from '../../../../components/RejectionNotesTimeline';
import { useCurrentUser } from '../../../../contexts/currentUser';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import useForm from '../../../../hooks/useForm';
import { useNavigationGuardBlocker } from '../../../../hooks/useNavigationGuard';
import {
  AppFile,
  CrewMemberCertificateStatus,
  CrewMemberRole,
} from '../../../../types/core';
import {
  CrewMember,
  CrewMemberCertificate,
  NewCrewMemberCertificate,
} from '../../../../types/crew';
import { Role } from '../../../../types/user';
import {
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_VISUALIZATION_DATE_FORMAT,
  getNullableDayjsDifferenceChecker,
} from '../../../../utils/date';
import VesselCertificateStatusBadge from '../../Vessels/VesselCertificateStatusBadge';
import CrewRejectionDialog from '../CrewRejectionDialog';
import useApiErrorMessageGetter from '../../../../hooks/useApiErrorMessage';

export interface CertificationCardElementProps {
  crewMember: CrewMember;
  certificate: CrewMemberCertificate | null;
  isLoading: boolean;
  onDelete: (hasPendingChanges: boolean) => void;
  onSave: (certificateValues: NewCrewMemberCertificate) => void;
  onBaseDataUpdate?: (baseData: CrewMemberCertificate) => void;
}

type CertificationFormValues = {
  type: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  files: AppFile[];
};

const CertificationCard = ({
  crewMember,
  certificate,
  isLoading,
  onDelete,
  onSave,
  onBaseDataUpdate,
}: CertificationCardElementProps) => {
  const { t } = useLanguage();
  const { hasRole, isOperator } = useCurrentUser();
  const { approveCrewMemberCertificate } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const [displayingRejectionDialog, setDisplayingRejectionDialog] =
    useState(false);

  const isAdmin = hasRole(Role.ADMIN);

  const onSuccessfulApproveReject = (certificate: CrewMemberCertificate) => {
    onBaseDataUpdate?.(certificate);
  };

  const approveCrewMemberCertificateMutation = useMutation({
    mutationFn: (certificateId: string) =>
      approveCrewMemberCertificate(crewMember.id, certificateId),
    onSuccess: certificate => {
      onSuccessfulApproveReject(certificate);
      enqueueSnackbar({
        variant: 'success',
        message: t('content.certificate-approval-success'),
      });
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(
          error,
          t('content.certificate-approval-error'),
        ),
      });
      approveCrewMemberCertificateMutation.reset();
    },
  });

  const initialValues = useMemo<CertificationFormValues>(() => {
    if (!certificate) {
      return {
        type: '',
        startDate: null,
        endDate: null,
        files: [],
      };
    }
    return {
      type: certificate.type.toString(),
      startDate: dayjs(certificate.startDate),
      endDate: dayjs(certificate.endDate),
      files: certificate.files,
    };
  }, [certificate]);

  const today = dayjs(new Date());

  const {
    values,
    errors,
    updateValues,
    touchField,
    haveValuesChanged,
    handleSubmit,
  } = useForm({
    initialValues,
    onSubmit: values => {
      onSave({
        type: values.type,
        startDate: values.startDate
          ? values.startDate.format(DAYJS_DATA_DATE_FORMAT)
          : '',
        endDate: values.endDate
          ? values.endDate.format(DAYJS_DATA_DATE_FORMAT)
          : '',
        files: values.files,
      });
    },
    validate: (values, setError) => {
      if (!values.type) {
        setError('type', t('validation.required-field'));
      }
      if (!values.startDate) {
        setError('startDate', t('validation.required-field'));
      } else if (!values.startDate.isValid()) {
        setError('startDate', t('validation.invalid-date'));
      } else if (values.startDate.isAfter(today, 'day')) {
        setError(
          'startDate',
          t('validation.date-after-reference-date', {
            REFERENCE_DATE: today.format(DAYJS_VISUALIZATION_DATE_FORMAT),
          }),
        );
      }
      if (!values.endDate) {
        setError('endDate', t('validation.required-field'));
      } else if (!values.endDate.isValid()) {
        setError('endDate', t('validation.invalid-date'));
      } else if (
        values.startDate &&
        (values.startDate.isSame(values.endDate, 'day') ||
          values.startDate.isAfter(values.endDate, 'day'))
      ) {
        setError('endDate', t('validation.invalid-end-date'));
      }
      if (!values.files.length) {
        setError('files', t('validation.required-file'));
      }
    },
    differenceChecker: {
      startDate: getNullableDayjsDifferenceChecker('day'),
      endDate: getNullableDayjsDifferenceChecker('day'),
    },
    resetOnInitialValuesChange: () => !!certificate,
  });

  useNavigationGuardBlocker(() => haveValuesChanged);

  // TODO: Enable edition
  const areInputsDisabled = isLoading || Boolean(certificate);

  const shouldShowDeleteButton =
    crewMember.crewMemberRole === CrewMemberRole.Skipper &&
    (!certificate ||
      isAdmin ||
      (!isAdmin &&
        certificate.status === CrewMemberCertificateStatus.Rejected));

  const shouldShowApproveRejectButtons =
    certificate?.status === CrewMemberCertificateStatus.PendingApproval &&
    !isOperator;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card variant="outlined">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <header className="col-span-full font-bold flex gap-4">
            <span>{t('content.certification')}</span>
            <>
              {certificate ? (
                <VesselCertificateStatusBadge
                  status={certificate?.status}
                  isExpired={certificate?.isExpired}
                />
              ) : (
                <CustomChip small label={t('content.draft')} />
              )}
              {shouldShowDeleteButton && (
                <IconButton
                  disabled={isLoading}
                  onClick={() => onDelete(haveValuesChanged)}
                  className="ml-auto"
                >
                  <SvgIcon component={RiDeleteBinLine} />
                </IconButton>
              )}
            </>
          </header>
          <label className="block w-full">
            <InputHeader required>{t('content.type')}</InputHeader>
            <TextField
              placeholder={t('content.type')}
              name="type"
              value={values.type}
              onChange={event => updateValues({ type: event.target.value })}
              onBlur={() => touchField('type')}
              error={Boolean(errors.type)}
              helperText={errors.type}
              disabled={areInputsDisabled}
            />
          </label>
          <div className="flex flex-col lg:flex-row gap-4">
            <label className="block w-full">
              <InputHeader required>{t('content.start-date')}</InputHeader>
              <FormControl error={Boolean(errors.startDate)}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.startDate),
                      onBlur: () => touchField('startDate'),
                      disabled: areInputsDisabled,
                    },
                  }}
                  value={values.startDate}
                  maxDate={
                    values.endDate && values.endDate.isBefore(today)
                      ? values.endDate
                      : today
                  }
                  onChange={newValue =>
                    updateValues({
                      startDate: newValue,
                    })
                  }
                  disabled={areInputsDisabled}
                />
                {errors.startDate && (
                  <FormHelperText>{errors.startDate}</FormHelperText>
                )}
              </FormControl>
            </label>
            <label className="block w-full">
              <InputHeader required>{t('content.end-date')}</InputHeader>
              <FormControl error={Boolean(errors.endDate)}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.endDate),
                      onBlur: () => touchField('endDate'),
                    },
                  }}
                  value={values.endDate}
                  minDate={values.startDate ?? today}
                  onChange={newValue =>
                    updateValues({
                      endDate: newValue,
                    })
                  }
                  disabled={areInputsDisabled}
                />
                {errors.endDate && (
                  <FormHelperText>{errors.endDate}</FormHelperText>
                )}
              </FormControl>
            </label>
          </div>
          <div>
            <InputHeader required>{t('content.files')}</InputHeader>
            <span className="text-iots-grey-500 text-sm">
              {t('content.files-accepted-formats')}
            </span>
            <FormControl error={Boolean(errors.files)}>
              <FileUploader
                isError={Boolean(errors.files)}
                onChange={(validFiles: AppFile[]) => {
                  updateValues({
                    files: [...values.files, ...validFiles],
                  });
                }}
                onDelete={(id: string) => {
                  updateValues({
                    files: values.files.filter(file => file.id !== id),
                  });
                }}
                files={values.files}
                disabled={areInputsDisabled}
              />
              {errors.files && <FormHelperText>{errors.files}</FormHelperText>}
            </FormControl>
          </div>
          <div>
            {certificate?.notes && (
              <RejectionNotesTimeline rejectionNotes={certificate.notes} />
            )}
          </div>
          {shouldShowApproveRejectButtons && (
            <div className="flex gap-4 justify-end">
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={() => setDisplayingRejectionDialog(true)}
                disabled={isLoading}
              >
                {t('content.reject')}
              </Button>
              <Button
                type="button"
                variant="outlined"
                onClick={() =>
                  approveCrewMemberCertificateMutation.mutate(
                    certificate?.id || '',
                  )
                }
                loading={isLoading}
                disabled={isLoading}
              >
                {t('content.approve')}
              </Button>
              <CrewRejectionDialog
                crewMemberId={displayingRejectionDialog ? crewMember.id : null}
                certificateId={certificate.id}
                onClose={() => setDisplayingRejectionDialog(false)}
                onConfirm={rejectedCertificate => {
                  setDisplayingRejectionDialog(false);
                  onSuccessfulApproveReject(rejectedCertificate);
                }}
              />
            </div>
          )}
          {!shouldShowApproveRejectButtons && (
            <div className="flex justify-end">
              <Button
                variant="contained"
                type="submit"
                disabled={
                  !haveValuesChanged ||
                  areInputsDisabled ||
                  Object.values(errors).length > 0
                }
              >
                {t('content.save')}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </LocalizationProvider>
  );
};

export default CertificationCard;
